import React, { useMemo, useState, useCallback } from 'react'
import './order-filter.scss'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Row } from '@/components/layout/layout'
import { Radio, Spin } from 'antd'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import { IStaffInfo } from '@/model/types/staff'
import useSelectList from '@/utils/hooks/useSelectList'
import classnames from 'classnames'
import Iconfont from '@/components/iconfont/iconfont'
import FilterContainer from '../../../goods-manage/components/filter-container/filter-container'

const paymetList = [
    {
        id: 4,
        name: '现金',
    },
    {
        id: 1,
        name: '微信',
    },
    {
        id: 2,
        name: '支付宝',
    },
    {
        id: 9,
        name: '云闪付',
    },
    {
        id: 3,
        name: '余额',
    },
    {
        id: 7,
        name: '银行卡',
    },
    {
        id: 6,
        name: '个人支付宝',
    },
    {
        id: 5,
        name: '个人微信',
    },
]

export interface IOrderCashierFilterData {
    staffList: IStaffInfo[]
    paymentList: Array<{ id: number; name: string; is_custom_pay_type?: boolean }>
    orderStatus: number
}

interface OrderCashierFilterProps extends IOrderCashierFilterData {
    onConfirm: (data: IOrderCashierFilterData) => void
    onCancel: () => void
}

const OrderCashierFilter: React.FC<OrderCashierFilterProps> = props => {
    const cls = useBemClass('order-cashier-filter')

    const [orderStatus, setOrderStatus] = useState<number>(props.orderStatus)

    const customPaymentData = useRequest<{
        custom_payment_config: Array<{
            id: number
            name: string
            des: string
        }>
    }>(
        () =>
            $post({
                url: '/shop/getshoppayment',
            }),
        {
            loadingDelay: 200, // 加载延时，防止闪烁
        },
    )

    const staffListData = useRequest<{
        staff_list: IStaffInfo[]
    }>(() =>
        $post({
            url: '/staff/getstafflist',
            params: {
                limit: 100,
                status: 0,
            },
        }),
    )

    const paymentTypeList = useMemo(() => {
        if (!customPaymentData.data) {
            return paymetList
        } else {
            return [
                ...paymetList,
                ...customPaymentData.data.custom_payment_config.map(p => ({
                    id: p.id,
                    name: p.name,
                    is_custom_pay_type: true, // 消除魔法字符串逻辑，使用单独字段来表示是否是自定义支付方式(方便调接口时区分默认、自定义支付方式参数)
                })),
            ]
        }
    }, [customPaymentData.data])

    const paymentSelectAction = useSelectList<{ id: number; name: string }>(props.paymentList, 'id')
    const staffSelectAction = useSelectList<IStaffInfo>(props.staffList, 'id')

    const reset = useCallback(() => {
        paymentSelectAction.setSelect([])
        staffSelectAction.setSelect([])
        setOrderStatus(0)
    }, [paymentSelectAction, staffSelectAction])

    return (
        <FilterContainer
            onConfirm={() => {
                props.onConfirm({
                    orderStatus,
                    staffList: staffSelectAction.select,
                    paymentList:
                        paymentSelectAction.select.length === paymentTypeList.length ? [] : paymentSelectAction.select,
                })
                props.onCancel()
            }}
            onCancel={props.onCancel}
            onReset={() => {
                reset()
            }}
        >
            <Spin spinning={customPaymentData.loading || staffListData.loading}>
                <Row data-align-items-center>
                    <div data-color-999 data-font-size-14 data-margin-right-25>
                        订单类型
                    </div>
                    <Radio.Group
                        value={orderStatus}
                        onChange={event => {
                            setOrderStatus(event.target.value)
                        }}
                    >
                        <Radio value={0}>全部</Radio>
                        <Radio name="allow_multiple_units" value={2}>
                            退款
                        </Radio>
                        <Radio name="allow_multiple_units" value={1}>
                            已结算
                        </Radio>
                    </Radio.Group>
                </Row>
                <Row>
                    <div
                        data-color-999
                        data-font-size-14
                        data-margin-right-25
                        style={{
                            flexShrink: 0,
                            marginTop: '26px',
                        }}
                    >
                        支付方式
                    </div>
                    <Row
                        style={{
                            flexWrap: 'wrap',
                            alignItems: 'center',
                        }}
                    >
                        {paymentTypeList.map(item => (
                            <div
                                className={classnames(cls('tag'), {
                                    [cls('tag', 'active')]: paymentSelectAction.has(item),
                                })}
                                key={item.id}
                                onClick={() => {
                                    paymentSelectAction.toggle(item)
                                }}
                            >
                                <div hidden={!paymentSelectAction.has(item)} className={cls('tag', 'icon')}>
                                    <Iconfont name="iconyixuan" size={14} />
                                </div>
                                <span>{item.name}</span>
                            </div>
                        ))}
                    </Row>
                </Row>
                <Row>
                    <div
                        data-color-999
                        data-font-size-14
                        data-margin-right-38
                        style={{
                            flexShrink: 0,
                            marginTop: '26px',
                        }}
                    >
                        收银员
                    </div>
                    <Row
                        style={{
                            flexWrap: 'wrap',
                            alignItems: 'center',
                        }}
                    >
                        {(staffListData.data?.staff_list || []).map(item => (
                            <div
                                className={classnames(cls('tag'), {
                                    [cls('tag', 'active')]: staffSelectAction.has(item),
                                })}
                                key={item.id}
                                onClick={() => {
                                    staffSelectAction.toggle(item)
                                }}
                            >
                                <div hidden={!staffSelectAction.has(item)} className={cls('tag', 'icon')}>
                                    <Iconfont name="iconyixuan" size={14} />
                                </div>
                                <img
                                    alt=""
                                    src={item.user.avatar}
                                    width="24"
                                    height="24"
                                    data-border-radius-50p
                                    data-margin-right-8
                                />
                                <span className={cls('tag', 'nickname')}>{item.user.nickname}</span>
                                {item.user.realname && (
                                    <span className={cls('tag', 'realname')}>({item.user.realname})</span>
                                )}
                            </div>
                        ))}
                    </Row>
                </Row>
            </Spin>
        </FilterContainer>
    )
}

export default OrderCashierFilter
